import crypto from 'crypto';
import { makeAutoObservable } from 'mobx';
import { formatTimestampToDate, FormatOptions, Validator } from '@frontend-monorepo/cyolo-utils';
import { MfaId } from '@frontend-monorepo/cyolo-auth';
import { HttpStatusCode, Store } from '@frontend-monorepo/cyolo-store';
import axios from '@frontend-monorepo/http-client';

// Unique ID creation requires a high quality random # generator.  In node.js
// this is pretty straight-forward - we use the crypto API.



var rng = function nodeRNG() {
  return crypto.randomBytes(16);
};

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */
var byteToHex = [];
for (var i = 0; i < 256; ++i) {
  byteToHex[i] = (i + 0x100).toString(16).substr(1);
}

function bytesToUuid(buf, offset) {
  var i = offset || 0;
  var bth = byteToHex;
  // join used to fix memory issue caused by concatenation: https://bugs.chromium.org/p/v8/issues/detail?id=3175#c4
  return ([
    bth[buf[i++]], bth[buf[i++]],
    bth[buf[i++]], bth[buf[i++]], '-',
    bth[buf[i++]], bth[buf[i++]], '-',
    bth[buf[i++]], bth[buf[i++]], '-',
    bth[buf[i++]], bth[buf[i++]], '-',
    bth[buf[i++]], bth[buf[i++]],
    bth[buf[i++]], bth[buf[i++]],
    bth[buf[i++]], bth[buf[i++]]
  ]).join('');
}

var bytesToUuid_1 = bytesToUuid;

// **`v1()` - Generate time-based UUID**
//
// Inspired by https://github.com/LiosK/UUID.js
// and http://docs.python.org/library/uuid.html

var _nodeId;
var _clockseq;

// Previous uuid creation time
var _lastMSecs = 0;
var _lastNSecs = 0;

// See https://github.com/uuidjs/uuid for API details
function v1(options, buf, offset) {
  var i = buf && offset || 0;
  var b = buf || [];

  options = options || {};
  var node = options.node || _nodeId;
  var clockseq = options.clockseq !== undefined ? options.clockseq : _clockseq;

  // node and clockseq need to be initialized to random values if they're not
  // specified.  We do this lazily to minimize issues related to insufficient
  // system entropy.  See #189
  if (node == null || clockseq == null) {
    var seedBytes = rng();
    if (node == null) {
      // Per 4.5, create and 48-bit node id, (47 random bits + multicast bit = 1)
      node = _nodeId = [
        seedBytes[0] | 0x01,
        seedBytes[1], seedBytes[2], seedBytes[3], seedBytes[4], seedBytes[5]
      ];
    }
    if (clockseq == null) {
      // Per 4.2.2, randomize (14 bit) clockseq
      clockseq = _clockseq = (seedBytes[6] << 8 | seedBytes[7]) & 0x3fff;
    }
  }

  // UUID timestamps are 100 nano-second units since the Gregorian epoch,
  // (1582-10-15 00:00).  JSNumbers aren't precise enough for this, so
  // time is handled internally as 'msecs' (integer milliseconds) and 'nsecs'
  // (100-nanoseconds offset from msecs) since unix epoch, 1970-01-01 00:00.
  var msecs = options.msecs !== undefined ? options.msecs : new Date().getTime();

  // Per 4.2.1.2, use count of uuid's generated during the current clock
  // cycle to simulate higher resolution clock
  var nsecs = options.nsecs !== undefined ? options.nsecs : _lastNSecs + 1;

  // Time since last uuid creation (in msecs)
  var dt = (msecs - _lastMSecs) + (nsecs - _lastNSecs)/10000;

  // Per 4.2.1.2, Bump clockseq on clock regression
  if (dt < 0 && options.clockseq === undefined) {
    clockseq = clockseq + 1 & 0x3fff;
  }

  // Reset nsecs if clock regresses (new clockseq) or we've moved onto a new
  // time interval
  if ((dt < 0 || msecs > _lastMSecs) && options.nsecs === undefined) {
    nsecs = 0;
  }

  // Per 4.2.1.2 Throw error if too many uuids are requested
  if (nsecs >= 10000) {
    throw new Error('uuid.v1(): Can\'t create more than 10M uuids/sec');
  }

  _lastMSecs = msecs;
  _lastNSecs = nsecs;
  _clockseq = clockseq;

  // Per 4.1.4 - Convert from unix epoch to Gregorian epoch
  msecs += 12219292800000;

  // `time_low`
  var tl = ((msecs & 0xfffffff) * 10000 + nsecs) % 0x100000000;
  b[i++] = tl >>> 24 & 0xff;
  b[i++] = tl >>> 16 & 0xff;
  b[i++] = tl >>> 8 & 0xff;
  b[i++] = tl & 0xff;

  // `time_mid`
  var tmh = (msecs / 0x100000000 * 10000) & 0xfffffff;
  b[i++] = tmh >>> 8 & 0xff;
  b[i++] = tmh & 0xff;

  // `time_high_and_version`
  b[i++] = tmh >>> 24 & 0xf | 0x10; // include version
  b[i++] = tmh >>> 16 & 0xff;

  // `clock_seq_hi_and_reserved` (Per 4.2.2 - include variant)
  b[i++] = clockseq >>> 8 | 0x80;

  // `clock_seq_low`
  b[i++] = clockseq & 0xff;

  // `node`
  for (var n = 0; n < 6; ++n) {
    b[i + n] = node[n];
  }

  return buf ? buf : bytesToUuid_1(b);
}

var v1_1 = v1;

function v4(options, buf, offset) {
  var i = buf && offset || 0;

  if (typeof(options) == 'string') {
    buf = options === 'binary' ? new Array(16) : null;
    options = null;
  }
  options = options || {};

  var rnds = options.random || (options.rng || rng)();

  // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`
  rnds[6] = (rnds[6] & 0x0f) | 0x40;
  rnds[8] = (rnds[8] & 0x3f) | 0x80;

  // Copy bytes to buffer, if provided
  if (buf) {
    for (var ii = 0; ii < 16; ++ii) {
      buf[i + ii] = rnds[ii];
    }
  }

  return buf || bytesToUuid_1(rnds);
}

var v4_1 = v4;

var uuid = v4_1;
uuid.v1 = v1_1;
uuid.v4 = v4_1;

var uuid_1 = uuid;

/**
 * a slim description of an identity provider
 */
class IdentityProviderDescriptor {
    /**
     * returns a new instance of a ipd
     * @param id
     * @param typeUniqueId
     * @param idpType
     * @param idpName
     * @param visible
     */
    constructor(id = uuid_1.v1(), typeUniqueId = '', // id is global unique idp id while typeUniqueId is a local table id
    idpType = 'local', idpName = '', visible = true) {
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: id
        });
        Object.defineProperty(this, "typeUniqueId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: typeUniqueId
        });
        Object.defineProperty(this, "idpType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: idpType
        });
        Object.defineProperty(this, "idpName", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: idpName
        });
        Object.defineProperty(this, "visible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: visible
        });
        makeAutoObservable(this, {}, { autoBind: true });
    }
    /**
     * parses raw object
     * @param res
     * @returns
     */
    static fromRaw(res) {
        return new IdentityProviderDescriptor(String(res.id), String(res.idp_id), res.idp_kind, res.name, res.visible);
    }
    /**
     * converts this ipd to a raw object
     * @returns
     */
    toRaw() {
        const { typeUniqueId, id, idpType, idpName, visible } = this;
        return {
            id: Number(id),
            idp_id: Number(typeUniqueId),
            idp_kind: String(idpType),
            name: idpName,
            visible: visible,
        };
    }
    /**
     * converts a identity provider into a more general ipd
     * @param idp
     * @returns
     */
    static fromIdentityProvider(idp) {
        return new IdentityProviderDescriptor(idp.id, idp.typeUniqueId, String(idp.idpType), idp.idpName, idp.visible);
    }
    /**
     * clones a instance
     * @returns
     */
    clone() {
        return new IdentityProviderDescriptor(this.id, this.typeUniqueId, this.idpType, this.idpName, this.visible);
    }
    get formattedIdpType() {
        switch (this.idpType) {
            case 'openid':
                return 'OpenID';
            case 'ldap':
                return 'LDAP';
            case 'radius':
                return 'Radius';
            case 'saml':
                return 'SAML';
            case 'local':
                return 'Local';
            default:
                return this.idpType;
        }
    }
}

var UserDefinitionType;
(function (UserDefinitionType) {
    UserDefinitionType["AnonymousUsers"] = "Anonymous identities";
    UserDefinitionType["AnyAuthenticatedUser"] = "Any authenticated identity";
    UserDefinitionType["SpecificUsers"] = "Specific identities";
    UserDefinitionType["None"] = "None";
})(UserDefinitionType || (UserDefinitionType = {}));

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const ZeroValueText = 'Never';
const formatDate = (timestamp) => {
    return formatTimestampToDate(timestamp, Object.assign(Object.assign({}, new FormatOptions()), { zeroValueText: ZeroValueText }));
};

const Endpoints = {
    GetUsers: '/v1/users',
    GetUserData: '/v1/users/me',
    CreateUser: '/v1/users',
    DeleteUsersBulk: '/v1/users/bulk',
    CreatUserBulk: '/v1/users/bulk',
    PATCH_UpdatePassword: '/v1/users/me/password/change',
    UpdateUser(id) {
        return `/v1/users/${id}`;
    },
    DeleteUser(id) {
        return `/v1/users/${id}`;
    },
    ResetEnrollmentBulk: '/v1/users/reset-enrollment',
};

const getMultipleUsers = () => __awaiter(void 0, void 0, void 0, function* () {
    const { data } = yield axios.get(Endpoints.GetUsers);
    return data;
});
const createUser = (raw) => __awaiter(void 0, void 0, void 0, function* () {
    yield axios.put(Endpoints.CreateUser, raw);
});
const createUsers = (raws) => __awaiter(void 0, void 0, void 0, function* () {
    yield axios.put(Endpoints.CreatUserBulk, raws);
});
const updateUser = (userId, raw) => __awaiter(void 0, void 0, void 0, function* () {
    yield axios.post(Endpoints.UpdateUser(userId), raw);
});
const deleteUser = (userId) => __awaiter(void 0, void 0, void 0, function* () {
    yield axios.delete(Endpoints.DeleteUser(userId));
});
const deleteMultipleUsers = (userIds) => __awaiter(void 0, void 0, void 0, function* () {
    yield axios.delete(Endpoints.DeleteUsersBulk, { data: userIds });
});
const getUserData = () => __awaiter(void 0, void 0, void 0, function* () {
    const { data } = yield axios.get(Endpoints.GetUserData, {
        validateStatus: (status) => {
            // if we get a 403 we ignore it explicitly
            return (status < HttpStatusCode.ClientErrorCodeStart ||
                status === HttpStatusCode.UserAnon);
        },
    });
    return data;
});
const resetEnrollmentBulk = (userIds) => __awaiter(void 0, void 0, void 0, function* () {
    yield axios.post(Endpoints.ResetEnrollmentBulk, userIds);
});
var UserAPI = {
    getUserData,
    getMultipleUsers,
    createUser,
    createUsers,
    updateUser,
    deleteUser,
    deleteMultipleUsers,
    resetEnrollmentBulk,
};

var UserAttributeName;
(function (UserAttributeName) {
    UserAttributeName["AcceptedLegalDocumentation"] = "accepted_legal_doc";
})(UserAttributeName || (UserAttributeName = {}));

/**
 * A system user entity
 */
var Status;
(function (Status) {
    Status["WAITING_FOR_ENROLLMENT"] = "Waiting for enrollment";
    Status["ACTIVE"] = "Active";
})(Status || (Status = {}));
class User {
    /**
     * returns a new user instance
     * @param id
     * @param name
     * @param firstName
     * @param lastName
     * @param phoneNumber
     * @param email
     * @param personalDesktop
     * @param enrolled
     * @param enabled
     * @param enrollmentMethods
     * @param identityProviders
     * @param system
     * @param supervisor
     * @param totpEnrolled
      //TODO: https://cyolo.atlassian.net/browse/CY-10713
     * @param lastLogin
     * @param password
     * @param labels
     * @param mustChangePassword
     * @param sendEmail
     * @param totpSecret
     * @param inviteUser
     * @param mfaValidPolicies
     * @param emailEnrolled
     * @param smsEnrolled
     * @param lastLogout
     * @param simpleGroups
     * @param externalGroups
     //TODO: https://cyolo.atlassian.net/browse/CY-10713
     * @param lastSeen
     */
    constructor(id = `${Date.now()}`, name = '', firstName = '', lastName = '', phoneNumber = '', email = '', personalDesktop = '', enrolled = false, enabled = false, enrollmentMethods = [], identityProviders = [], system = false, supervisor = undefined, totpEnrolled = false, lastLogin = '', labels = [], password = '', mustChangePassword = false, sendEmail = false, totpSecret, inviteUser = false, mfaValidPolicies = [], emailEnrolled = false, smsEnrolled = false, lastLogout = '', simpleGroups = [], externalGroups = [], lastSeen = '') {
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: id
        });
        Object.defineProperty(this, "name", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: name
        });
        Object.defineProperty(this, "firstName", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: firstName
        });
        Object.defineProperty(this, "lastName", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: lastName
        });
        Object.defineProperty(this, "phoneNumber", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: phoneNumber
        });
        Object.defineProperty(this, "email", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: email
        });
        Object.defineProperty(this, "personalDesktop", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: personalDesktop
        });
        Object.defineProperty(this, "enrolled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: enrolled
        });
        Object.defineProperty(this, "enabled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: enabled
        });
        Object.defineProperty(this, "enrollmentMethods", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: enrollmentMethods
        });
        Object.defineProperty(this, "identityProviders", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: identityProviders
        });
        Object.defineProperty(this, "system", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: system
        });
        Object.defineProperty(this, "supervisor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: supervisor
        });
        Object.defineProperty(this, "totpEnrolled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: totpEnrolled
        });
        Object.defineProperty(this, "lastLogin", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: lastLogin
        });
        Object.defineProperty(this, "labels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: labels
        });
        Object.defineProperty(this, "password", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: password
        });
        Object.defineProperty(this, "mustChangePassword", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: mustChangePassword
        });
        Object.defineProperty(this, "sendEmail", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: sendEmail
        });
        Object.defineProperty(this, "totpSecret", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: totpSecret
        });
        Object.defineProperty(this, "inviteUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: inviteUser
        });
        Object.defineProperty(this, "mfaValidPolicies", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: mfaValidPolicies
        });
        Object.defineProperty(this, "emailEnrolled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: emailEnrolled
        });
        Object.defineProperty(this, "smsEnrolled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: smsEnrolled
        });
        Object.defineProperty(this, "lastLogout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: lastLogout
        });
        Object.defineProperty(this, "simpleGroups", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: simpleGroups
        });
        Object.defineProperty(this, "externalGroups", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: externalGroups
        });
        Object.defineProperty(this, "lastSeen", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: lastSeen
        });
        //this property was added to have the ability to filter by "Status" field in table
        Object.defineProperty(this, "kind", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'user'
        });
        //TODO: https://cyolo.atlassian.net/browse/CY-10713
        Object.defineProperty(this, "status", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: Status.WAITING_FOR_ENROLLMENT
        });
        makeAutoObservable(this, {}, { autoBind: true });
        this.status = enrolled ? Status.ACTIVE : Status.WAITING_FOR_ENROLLMENT;
        //TODO: This is added to enable proper display/filter/suggestion
        // of date time values in general and zero values in particular
        //TODO: https://cyolo.atlassian.net/browse/CY-10713
        this.lastLogin = formatDate(this.lastLogin);
        this.lastSeen = formatDate(this.lastLogout);
    }
    /**
     * a composite id made of kind & id
     */
    get systemUniqueID() {
        return `${this.kind}_${this.id}`;
    }
    /**
     * returns if this user instance content is valid
     */
    get isValid() {
        // validate descriptors
        if (this.identityProviders.length === 0)
            return false;
        // validate password
        if (this.canResetPassword && !Validator.complexPassword(this.password)) {
            return false;
        }
        // validate phone number if it exists
        if (this.phoneNumber.length > 0) {
            if (!Validator.phoneNumber(this.phoneNumber))
                return false;
        }
        // validate email if it exists
        if (this.email.length > 0 && !Validator.email(this.email)) {
            return false;
        }
        // validate username
        if (!Validator.notEmpty(this.name))
            return false;
        return true;
    }
    /**
     * returns a formatted version of the user idp names
     */
    get formattedIdentityProviders() {
        return this.identityProviders.map((idp) => idp.idpName).join(', ');
    }
    get selectedIdp() {
        if (this.identityProviders.length === 0)
            return undefined;
        return this.identityProviders[0];
    }
    get canResetPassword() {
        if (this.identityProviders.length === 0)
            return false;
        return this.identityProviders[0].idpType === 'local';
    }
    get personalDesktopList() {
        return this.personalDesktop ? this.personalDesktop.split(',') : [];
    }
    /**
     * returns a new empty user instance
     * @returns
     */
    static EmptyUser() {
        return new User(uuid_1.v1(), '', '', '', '', '', '', false, true, [], [], false, undefined, undefined, '', [], '', true, true);
    }
    /**
     * parses a raw user object
     * @param res
     * @returns
     */
    static fromRaw(res) {
        var _a;
        const { id, name, first_name, last_name, phone_number, email, personal_desktop, enrolled, enabled, allowed_idps, sms_enrolled, totp_enrolled, email_enrolled, system, supervisor, last_login, labels, must_change_password, send_email, invite_user, mfa_valid_policies, last_logout, simple_groups, external_groups, } = res;
        // parse out identity providers
        const identityProviders = (_a = allowed_idps === null || allowed_idps === void 0 ? void 0 : allowed_idps.map(IdentityProviderDescriptor.fromRaw)) !== null && _a !== void 0 ? _a : [];
        const supervisorUser = supervisor === null ? undefined : User.fromRaw(supervisor);
        // calculate the user mfa enrolled ids
        const mfaEnrolledIds = [];
        if (sms_enrolled)
            mfaEnrolledIds.push(MfaId.SMS);
        if (email_enrolled)
            mfaEnrolledIds.push(MfaId.EMAIL);
        if (totp_enrolled)
            mfaEnrolledIds.push(MfaId.TOTP);
        return new User(id, name, first_name, last_name, phone_number, email, personal_desktop, enrolled, enabled, mfaEnrolledIds, identityProviders, system, supervisorUser, totp_enrolled, last_login !== null && last_login !== void 0 ? last_login : '', labels, '', must_change_password, send_email, '', invite_user, mfa_valid_policies, email_enrolled, sms_enrolled, last_logout, simple_groups, external_groups);
    }
    /**
     * setter for identity providers
     * @param idps
     */
    setIDP(...idps) {
        this.identityProviders = idps;
        // resets password when the idp kind is not local
        if (!this.canResetPassword) {
            this.password = '';
        }
    }
    setPassword(password) {
        this.password = password;
    }
    didPassPolicyMfa(policyId) {
        return this.mfaValidPolicies.includes(policyId);
    }
    /**
     * converts this user into a raw
     * user object used for server updates
     * @returns
     */
    toRaw() {
        // if user is supervised, change enrolled and totpEnrolled to true
        if (this.supervisor != undefined) {
            this.enrolled = true;
            this.totpEnrolled = true;
        }
        let rawSupervisor;
        if (this.supervisor === undefined) {
            rawSupervisor = null;
        }
        else {
            rawSupervisor = Number(this.supervisor.id);
        }
        const rawObject = {
            allowed_idps: this.identityProviders.map((idp) => Number(idp.id)),
            enabled: this.enabled,
            enrolled: this.enrolled,
            name: this.name,
            first_name: this.firstName,
            last_name: this.lastName,
            personal_desktop: this.personalDesktopList,
            phone_number: this.phoneNumber,
            email: this.email,
            totp_enrolled: this.totpEnrolled,
            supervisor: rawSupervisor,
            must_change_password: this.mustChangePassword,
            send_email: this.sendEmail,
            invite_user: this.inviteUser,
            email_enrolled: this.emailEnrolled,
            sms_enrolled: this.smsEnrolled,
        };
        // if password is defined add it to raw object
        if (this.password.length > 0) {
            rawObject.password = this.password;
        }
        if (this.totpSecret !== undefined) {
            rawObject.totp_secret = this.totpSecret;
        }
        return rawObject;
    }
    /**
     * deletes the user instance
     * @returns
     */
    delete() {
        return __awaiter(this, void 0, void 0, function* () {
            return UserAPI.deleteUser(this.id);
        });
    }
    /**
     * updates the user instance
     * @returns
     */
    update() {
        return __awaiter(this, void 0, void 0, function* () {
            return UserAPI.updateUser(this.id, this.toRaw());
        });
    }
    /**
     * creates the user instance
     * @returns
     */
    create() {
        return __awaiter(this, void 0, void 0, function* () {
            return UserAPI.createUser(this.toRaw());
        });
    }
}
/**
 * static system names
 */
Object.defineProperty(User, "SystemUserNames", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: {
        Anonymous: 'anonymous',
        Authenticated: 'authenticated',
        Admin: 'admin',
    }
});

class CurrentUserStore extends Store {
    constructor() {
        super('current_user', () => __awaiter(this, void 0, void 0, function* () {
            return User.fromRaw(yield UserAPI.getUserData());
        }), User.EmptyUser());
    }
}

export { CurrentUserStore, IdentityProviderDescriptor, Status, User, UserAPI, UserAttributeName, UserDefinitionType, ZeroValueText, formatDate };
//# sourceMappingURL=index.es.js.map
